import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { ArrowLeft, Lock, Unlock, Video, Mic, MessageSquare, Send, MoreVertical, X, Sparkles, Heart, Image, Share2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePortalDetail, usePortalUpload, PortalCapsule, PortalMode, CapsuleType, usePortal } from "@/hooks/usePortal";
import EchoCapsuleModal from "./EchoCapsuleModal";
import CaptureSparkModal from "./CaptureSparkModal";
import PortalMemoryGallery from "./PortalMemoryGallery";
import { useAuthContext } from "@/contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface PortalDetailScreenProps {
  portalId: string;
  onBack: () => void;
}

const modeLabels: Record<PortalMode, string> = {
  serious: "Serious Dating",
  casual: "Fun & Casual",
  deep: "Deep Talk",
  anonymous: "Anonymous First",
};

// Mode-based background gradients and ambient sounds
const modeBackgrounds: Record<PortalMode, { gradient: string; ambient: string }> = {
  serious: {
    gradient: "from-blue-50 via-indigo-50 to-blue-100/50",
    ambient: "https://cdn.pixabay.com/download/audio/2021/11/09/audio_6c3cdd43aa.mp3?filename=cafe-ambience-ambient-7251.mp3",
  },
  casual: {
    gradient: "from-pink-50 via-rose-50 to-pink-100/50",
    ambient: "https://cdn.pixabay.com/download/audio/2022/02/23/audio_333f0cb1cf.mp3?filename=city-ambience-101189.mp3",
  },
  deep: {
    gradient: "from-purple-50 via-violet-50 to-purple-100/50",
    ambient: "https://cdn.pixabay.com/download/audio/2022/03/15/audio_b9f07a6a0b.mp3?filename=sea-waves-ambient-110397.mp3",
  },
  anonymous: {
    gradient: "from-slate-50 via-gray-50 to-slate-100/50",
    ambient: "https://cdn.pixabay.com/download/audio/2021/08/04/audio_5a3d11d3c0.mp3?filename=wind-ambient-6195.mp3",
  },
};

// Mode aura colors
const modeAuras: Record<PortalMode, string> = {
  serious: "ring-blue-500/60 bg-blue-500/15",
  casual: "ring-pink-400/60 bg-pink-400/15",
  deep: "ring-purple-500/60 bg-purple-500/15",
  anonymous: "ring-slate-500/60 bg-slate-500/15",
};

const PortalDetailScreen = ({ portalId, onBack }: PortalDetailScreenProps) => {
  const { user } = useAuthContext();
  const { portal, capsules, messages, loading, createCapsule, sendMessage } = usePortalDetail(portalId);
  const { uploadMedia, uploading } = usePortalUpload();
  const { closePortal } = usePortal();
  
  const [newMessage, setNewMessage] = useState("");
  const [showComposer, setShowComposer] = useState(false);
  const [composerType, setComposerType] = useState<CapsuleType>("prompt");
  const [promptText, setPromptText] = useState("");
  const [hasShownUnlockToast, setHasShownUnlockToast] = useState(false);
  
  // New features state
  const [observerMode, setObserverMode] = useState(true); // Observer Whisper mode
  const [observerSecondsLeft, setObserverSecondsLeft] = useState(30);
  const [showEchoCapsule, setShowEchoCapsule] = useState(false);
  const [showMemoryGallery, setShowMemoryGallery] = useState(false);
  const [showCaptureSpark, setShowCaptureSpark] = useState(false);
  const [hasEnteredBefore, setHasEnteredBefore] = useState(false);
  
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const echoFileInputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [capsules, messages]);

  // Show toast when chat unlocks with confetti
  useEffect(() => {
    if (portal?.chat_unlocked && !hasShownUnlockToast) {
      toast.success("Portal Resonance — Chat unlocked! ✨", {
        icon: "🎉",
        duration: 5000,
        description: "Your capsules resonated — the real conversation begins.",
      });
      setHasShownUnlockToast(true);
      
      // Auto-offer Capture Our Spark after unlock
      setTimeout(() => {
        setShowCaptureSpark(true);
      }, 2000);
    }
  }, [portal?.chat_unlocked, hasShownUnlockToast]);

  // Observer Whisper mode countdown (30s read-only on first entry)
  useEffect(() => {
    if (!portal || hasEnteredBefore) {
      setObserverMode(false);
      return;
    }

    // Check if user has entered this portal before (localStorage)
    const enteredPortals = JSON.parse(localStorage.getItem("entered_portals") || "[]");
    if (enteredPortals.includes(portalId)) {
      setHasEnteredBefore(true);
      setObserverMode(false);
      return;
    }

    // Start observer mode countdown
    if (observerMode && observerSecondsLeft > 0) {
      const timer = setInterval(() => {
        setObserverSecondsLeft((prev) => {
          if (prev <= 1) {
            setObserverMode(false);
            setHasEnteredBefore(true);
            const updated = [...enteredPortals, portalId];
            localStorage.setItem("entered_portals", JSON.stringify(updated));
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [portal, portalId, observerMode, observerSecondsLeft, hasEnteredBefore]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    await sendMessage(newMessage.trim());
    setNewMessage("");
  };

  // Portal Resonance detection (shared mode + basic sentiment)
  // Note: Actual unlock happens via database trigger, this is for UI feedback
  const hasResonance = useMemo(() => {
    if (!portal.chat_unlocked || capsules.length < 2) return false;
    
    const myCapsule = capsules.find(c => c.sender_user_id === user?.id);
    const theirCapsule = capsules.find(c => c.sender_user_id !== user?.id);
    
    if (!myCapsule || !theirCapsule) return false;
    
    // Both chose the same mode (already matched via portal.mode)
    const sameMode = true;
    
    // Basic sentiment check (simplified - in production, use AI)
    const myText = myCapsule.prompt_text?.toLowerCase() || "";
    const theirText = theirCapsule.prompt_text?.toLowerCase() || "";
    
    const positiveWords = ["love", "excited", "happy", "curious", "interested", "wonderful", "amazing"];
    const myPositive = positiveWords.some(word => myText.includes(word));
    const theirPositive = positiveWords.some(word => theirText.includes(word));
    
    return sameMode && (myPositive || theirPositive);
  }, [portal.chat_unlocked, capsules, user?.id]);

  const handleSendPromptCapsule = async () => {
    if (!promptText.trim()) return;
    const success = await createCapsule("prompt", undefined, promptText.trim());
    setPromptText("");
    setShowComposer(false);
    
    if (success) {
      // Check for resonance after sending (will be checked when portal updates)
      // The actual unlock happens via database trigger when both capsules exist
      toast.success("Capsule sent! 💫", {
        description: "Waiting for their capsule...",
      });
    }
  };

  const handleMediaUpload = async (type: "video" | "voice") => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");
    const isAudio = file.type.startsWith("audio/");
    
    if (!isVideo && !isAudio) {
      toast.error("Please select a video or audio file");
      return;
    }

    const type: CapsuleType = isVideo ? "video" : "voice";
    const url = await uploadMedia(file, type);
    
    if (url) {
      const success = await createCapsule(type, url);
      setShowComposer(false);
      
      if (success) {
        toast.success("Capsule sent! 💫", {
          description: "Waiting for their capsule...",
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!portal) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <p className="text-muted-foreground mb-4">Portal not found</p>
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const modeBg = modeBackgrounds[portal.mode];
  const modeAura = modeAuras[portal.mode];

  return (
    <div className={cn(
      "min-h-screen flex flex-col bg-gradient-to-br",
      modeBg.gradient
    )}>
      {/* Ambient Audio (hidden) */}
      <audio src={modeBg.ambient} loop autoPlay className="hidden" />

      {/* Observer Whisper Mode Overlay */}
      {observerMode && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-3xl p-8 max-w-sm mx-4 text-center space-y-4">
            <div className={cn(
              "w-20 h-20 rounded-full mx-auto flex items-center justify-center border-4 animate-pulse",
              modeAura
            )}>
              <Eye className="w-10 h-10 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Observer Whisper</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Take a moment to feel the energy of this Portal
              </p>
              <div className="text-3xl font-bold text-primary mb-2">
                {observerSecondsLeft}s
              </div>
              <p className="text-xs text-muted-foreground">
                Warming up gently...
              </p>
            </div>
            <Button
              onClick={() => {
                setObserverMode(false);
                setHasEnteredBefore(true);
                const enteredPortals = JSON.parse(localStorage.getItem("entered_portals") || "[]");
                localStorage.setItem("entered_portals", JSON.stringify([...enteredPortals, portalId]));
              }}
              variant="outline"
              className="w-full"
            >
              Enter Now
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-muted">
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {/* Aura glow around avatar */}
          <div className={cn(
            "relative p-1 rounded-full",
            modeAura
          )}>
            <Avatar className="w-10 h-10">
              <AvatarImage src={portal.other_user?.photo_url || undefined} />
              <AvatarFallback>{portal.other_user?.name?.[0] || "?"}</AvatarFallback>
            </Avatar>
            {portal.chat_unlocked && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white animate-pulse" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold truncate">{portal.other_user?.name}</h1>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className={cn("text-xs", `bg-gradient-to-r ${modeBg.gradient.split(" ")[0]} ${modeBg.gradient.split(" ")[2]}`)}>
                {modeLabels[portal.mode]}
              </Badge>
              {portal.chat_unlocked ? (
                <span className="text-xs text-green-600 flex items-center gap-1 font-medium">
                  <Sparkles className="w-3 h-3" /> Resonance unlocked
                </span>
              ) : (
                <span className="text-xs text-muted-foreground flex items-center gap-1 italic">
                  <Lock className="w-3 h-3" /> Warming up gently...
                </span>
              )}
            </div>
          </div>
          
          <button className="p-2 rounded-full hover:bg-muted">
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Chat Locked Banner - Enhanced with poetic microcopy */}
      {!portal.chat_unlocked && (
        <div className="mx-4 mt-4 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border-2 border-amber-500/30">
          <div className="flex items-start gap-3">
            <div className={cn(
              "p-2 rounded-lg",
              modeAura
            )}>
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-200 mb-1">
                Capsule received — your turn to share when ready
              </p>
              <p className="text-sm text-amber-700/80 dark:text-amber-300/80 italic">
                Chat unlocks after you both exchange a capsule. This keeps conversations intentional and meaningful.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Portal Resonance Banner (when unlocked) */}
      {portal.chat_unlocked && (
        <div className={cn(
          "mx-4 mt-4 p-4 rounded-xl border-2",
          hasResonance 
            ? "bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 border-green-500/30 animate-pulse"
            : "bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-blue-500/10 border-blue-500/30"
        )}>
          <div className="flex items-center gap-3">
            <Sparkles className={cn(
              "w-5 h-5",
              hasResonance ? "text-green-600" : "text-blue-600"
            )} />
            <div>
              <p className={cn(
                "font-semibold",
                hasResonance ? "text-green-800 dark:text-green-200" : "text-blue-800 dark:text-blue-200"
              )}>
                {hasResonance ? "Portal Resonance — Chat unlocked ✨" : "Chat unlocked"}
              </p>
              <p className={cn(
                "text-sm italic",
                hasResonance ? "text-green-700/80 dark:text-green-300/80" : "text-blue-700/80 dark:text-blue-300/80"
              )}>
                {hasResonance ? "Your capsules resonated — the real conversation begins." : "The real conversation begins."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Capsules & Messages Timeline */}
      <ScrollArea ref={scrollRef} className="flex-1 px-4 py-4">
        <div className="space-y-4 pb-4">
          {/* Capsules */}
          {capsules.map((capsule) => (
            <CapsuleItem
              key={capsule.id}
              capsule={capsule}
              isOwn={capsule.sender_user_id === user?.id}
            />
          ))}

          {/* Messages (only if chat unlocked) */}
          {portal.chat_unlocked && messages.map((msg) => (
            <MessageItem
              key={msg.id}
              text={msg.text}
              isOwn={msg.sender_user_id === user?.id}
              timestamp={msg.created_at}
            />
          ))}

          {capsules.length === 0 && messages.length === 0 && (
            <div className="py-12 text-center px-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Start by sending your first capsule</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Chat will unlock once you both send a capsule.
              </p>
              <div className="p-3 rounded-xl bg-muted/50 border border-dashed border-border">
                <p className="text-xs text-muted-foreground mb-1">Suggested first capsule:</p>
                <p className="text-sm italic">"What made you want to open this Portal with me?"</p>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Bottom Navigation Bar */}
      <div className="sticky bottom-0 z-20 bg-background/95 backdrop-blur-xl border-t border-border/50 p-3">
        <div className="flex items-center justify-center gap-2">
          {/* Send Capsule/Echo Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              if (portal.chat_unlocked) {
                setShowEchoCapsule(true);
              } else {
                setShowComposer(true);
              }
            }}
            className="rounded-full w-12 h-12"
            title={portal.chat_unlocked ? "Send Echo Capsule" : "Send Capsule"}
          >
            {portal.chat_unlocked ? <Sparkles className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
          </Button>

          {/* Chat Button (locked/unlocked) */}
          {portal.chat_unlocked ? (
            <div className="flex-1 flex gap-2">
              {showComposer && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setShowComposer(false);
                    setComposerType("prompt");
                  }}
                  className="flex-shrink-0 rounded-full"
                  title="Close Composer"
                >
                  <X className="w-5 h-5" />
                </Button>
              )}
              <Input
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                className="flex-1 rounded-full"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                size="icon"
                className="rounded-full"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setShowComposer(true)}
              className="flex-1 rounded-full"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Send Capsule
            </Button>
          )}

          {/* Capture Our Spark / Memories */}
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              if (portal.chat_unlocked) {
                setShowCaptureSpark(true);
              } else {
                setShowMemoryGallery(true);
              }
            }}
            className="rounded-full w-12 h-12"
            title={portal.chat_unlocked ? "Capture Our Spark" : "Portal Memories"}
          >
            <Heart className="w-5 h-5" />
          </Button>

          {/* Close Portal */}
          <Button
            variant="ghost"
            size="icon"
            onClick={async () => {
              if (confirm("Close this Portal? This will notify the other person respectfully.")) {
                const success = await closePortal(portalId);
                if (success) {
                  toast.info("Portal closed with care — thank you for the intention");
                  onBack();
                }
              }
            }}
            className="rounded-full w-12 h-12"
            title="Close Portal"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Composer Modal (for capsules when chat is locked) */}
      {showComposer && !portal.chat_unlocked && (
        <div className="border-t border-border bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">Create Capsule</h3>
            <button onClick={() => setShowComposer(false)} className="p-1 rounded-full hover:bg-muted">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setComposerType("prompt")}
              className={cn(
                "flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border transition-all",
                composerType === "prompt" ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <MessageSquare className="w-5 h-5" />
              <span className="text-xs">Prompt</span>
            </button>
            <button
              onClick={() => setComposerType("video")}
              className={cn(
                "flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border transition-all",
                composerType === "video" ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <Video className="w-5 h-5" />
              <span className="text-xs">Video</span>
            </button>
            <button
              onClick={() => setComposerType("voice")}
              className={cn(
                "flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border transition-all",
                composerType === "voice" ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <Mic className="w-5 h-5" />
              <span className="text-xs">Voice</span>
            </button>
          </div>

          {composerType === "prompt" ? (
            <div className="space-y-3">
              <Textarea
                placeholder="Write a prompt or question..."
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                rows={3}
              />
              <Button
                onClick={handleSendPromptCapsule}
                disabled={!promptText.trim()}
                className="w-full"
              >
                Send Prompt Capsule
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept={composerType === "video" ? "video/*" : "audio/*"}
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                variant="outline"
                className="w-full"
              >
                {uploading ? "Uploading..." : `Upload ${composerType === "video" ? "Video" : "Voice"}`}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Max {composerType === "video" ? "50MB" : "10MB"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Echo Capsule Modal */}
      {showEchoCapsule && (
        <EchoCapsuleModal
          isOpen={showEchoCapsule}
          onClose={() => setShowEchoCapsule(false)}
          portalId={portalId}
          onSave={() => {
            // Refresh capsules to show new echo capsule
            // The usePortalDetail hook will automatically refetch
            setShowEchoCapsule(false);
          }}
        />
      )}

      {/* Capture Our Spark Modal */}
      {showCaptureSpark && (
        <CaptureSparkModal
          isOpen={showCaptureSpark}
          onClose={() => setShowCaptureSpark(false)}
          portal={portal}
          capsules={capsules}
        />
      )}

      {/* Portal Memory Gallery */}
      {showMemoryGallery && (
        <PortalMemoryGallery
          isOpen={showMemoryGallery}
          onClose={() => setShowMemoryGallery(false)}
          portalId={portalId}
          portal={portal}
          capsules={capsules}
          messages={messages}
        />
      )}
    </div>
  );
};

// Capsule Item Component
const CapsuleItem = ({ capsule, isOwn }: { capsule: PortalCapsule; isOwn: boolean }) => {
  return (
    <div className={cn("flex", isOwn ? "justify-end" : "justify-start")}>
      <div className={cn(
        "max-w-[80%] rounded-2xl p-4",
        isOwn 
          ? "bg-primary text-primary-foreground rounded-br-md" 
          : "bg-muted rounded-bl-md"
      )}>
        <div className="flex items-center gap-2 mb-2">
          {capsule.type === "video" && <Video className="w-4 h-4" />}
          {capsule.type === "voice" && <Mic className="w-4 h-4" />}
          {capsule.type === "prompt" && <MessageSquare className="w-4 h-4" />}
          <span className="text-xs font-medium uppercase">
            {capsule.type} Capsule
          </span>
        </div>

        {capsule.type === "prompt" && capsule.prompt_text && (
          <p className="text-sm">{capsule.prompt_text}</p>
        )}

        {capsule.type === "video" && capsule.content_url && (
          <video
            src={capsule.content_url}
            controls
            className="rounded-lg max-w-full"
            style={{ maxHeight: 200 }}
          />
        )}

        {capsule.type === "voice" && capsule.content_url && (
          <audio src={capsule.content_url} controls className="w-full" />
        )}

        <p className={cn(
          "text-xs mt-2",
          isOwn ? "text-primary-foreground/70" : "text-muted-foreground"
        )}>
          {formatDistanceToNow(new Date(capsule.created_at), { addSuffix: true })}
        </p>
      </div>
    </div>
  );
};

// Message Item Component
const MessageItem = ({ text, isOwn, timestamp }: { text: string; isOwn: boolean; timestamp: string }) => {
  return (
    <div className={cn("flex", isOwn ? "justify-end" : "justify-start")}>
      <div className={cn(
        "max-w-[80%] rounded-2xl px-4 py-2",
        isOwn 
          ? "bg-primary text-primary-foreground rounded-br-md" 
          : "bg-muted rounded-bl-md"
      )}>
        <p className="text-sm">{text}</p>
        <p className={cn(
          "text-xs mt-1",
          isOwn ? "text-primary-foreground/70" : "text-muted-foreground"
        )}>
          {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
        </p>
      </div>
    </div>
  );
};

export default PortalDetailScreen;
